import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TOUR_STATE } from "@demandproai/shared";
import Constants from "../Constants";
import { setUser } from "../redux/actions/users";
import useAxios from "./useAxios";
import useUserSetting from "./useUserSetting";
import { shouldOfferRefresher } from "../tours/tourRefresherEligibility";

const TOURS_SETTING_KEY = "tours";

const hasEnded = (tourState) =>
   Boolean(tourState?.[TOUR_STATE.COMPLETED] || tourState?.[TOUR_STATE.DISMISSED]);

// The user record is the durable answer, but it arrives a fetch late, so a
// local copy keeps the tour from flashing on reload
const useTourCompletion = () => {
   const dispatch = useDispatch();
   const { putData } = useAxios();
   const [localTours, setLocalTours] = useUserSetting({ key: TOURS_SETTING_KEY, defaultValue: {} });
   const serverTours = useSelector((state) => state.users?.onboarding);
   const lastLoginAt = useSelector((state) => state.users?.lastLoginAt);
   const previousLoginAt = useSelector((state) => state.users?.previousLoginAt);

   const isTourPending = useCallback(
      (tourId) => !hasEnded(localTours?.[tourId]) && !hasEnded(serverTours?.[tourId]),
      [localTours, serverTours]
   );

   // Only the user record can answer this: the local copy is per browser, and a
   // long absence is exactly the case where it may not be the browser they used
   const isRefresherDue = useCallback(
      (tourId) =>
         shouldOfferRefresher({
            tourState: serverTours?.[tourId],
            lastLoginAt,
            previousLoginAt,
         }),
      [serverTours, lastLoginAt, previousLoginAt]
   );

   const markTourState = useCallback(
      async ({ tourId, state }) => {
         setLocalTours((previous) => ({
            ...previous,
            [tourId]: { ...previous?.[tourId], [state]: new Date().toISOString() },
         }));

         const { success, data } = await putData(Constants.ApiUrl.user.updateTourState, { tourId, state });

         if (success) {
            dispatch(setUser({ onboarding: data?.onboarding ?? {} }));
         }
      },
      [setLocalTours, putData, dispatch]
   );

   const completeTour = useCallback(
      (tourId) => markTourState({ tourId, state: TOUR_STATE.COMPLETED }),
      [markTourState]
   );

   const dismissTour = useCallback(
      (tourId) => markTourState({ tourId, state: TOUR_STATE.DISMISSED }),
      [markTourState]
   );

   const markRefresherOffered = useCallback(
      (tourId) => markTourState({ tourId, state: TOUR_STATE.REFRESHER_OFFERED }),
      [markTourState]
   );

   return { isTourPending, isRefresherDue, completeTour, dismissTour, markRefresherOffered };
};

export default useTourCompletion;
